import { useCallback, useEffect, useRef, useState } from 'react';

const COPIED_STATE_DURATION_MS = 1500;

function copyToClipboard(value: string): Promise<void> {
  if (navigator?.clipboard?.writeText) {
    return navigator.clipboard.writeText(value);
  }

  const textarea = document.createElement('textarea');

  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);

  return Promise.resolve();
}

export const useCopyToClipboard = (): {
  copy: (value: string) => Promise<void>;
  isCopied: boolean;
} => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copy = useCallback(async (value: string) => {
    await copyToClipboard(value);

    setIsCopied(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(
      () => setIsCopied(false),
      COPIED_STATE_DURATION_MS,
    );
  }, []);

  return { copy, isCopied };
};
